<template>
  <VPopper
    ref="popper"
    class="v-select"
    :class="{ 'v-popper--has-options': options.length > 0 }"
    lazy
    :offset="5"
    placement="bottom-start"
    transition="v-zoom-in-top"
    trigger="focus"
  >
    <template #reference>
      <!-- @vue-expect-error - seems to be a vue extension issue. -->
      <VInput v-bind="props" class="v-input--autocomplete v-input--select" @blur="hidePopper" @focus="fetch(model)" @keyup="$emit('keyup', $event)" @update:model-value="input" />
    </template>

    <ul v-if="options.length > 0" class="v-select-options" :style="{ width: `${optionsWidth}px` }">
      <!-- @vue-expect-error - seems to be a vue extension issue. -->
      <li v-for="(option, index) in options" :key="index" class="v-select-option" @mousedown="onClickOption(option)" v-text="option" />
    </ul>
  </VPopper>
</template>

<script setup lang="ts" generic="TModel extends string | number | null">
import { debounce } from 'lodash-es'
import { onMounted, ref, useTemplateRef } from 'vue'

import { useResizeObserver } from '../composables/useResizeObserver.ts'
import { type MaybePromise } from '../types.ts'
import VInput, { type Props as InputProps } from './VInput.vue'
import VPopper from './VPopper.vue'

interface Props extends InputProps {
  fetchSuggestions?: (value: any) => MaybePromise<TModel[]>
}

const props = withDefaults(defineProps<Props>(), {
  type: 'text',
  autocomplete: 'off',
  placeholder: '',
  label: '',
  disabled: null,
  clearable: false,
  readonly: false,
  focusable: true,
  min: undefined,
  max: undefined,
  maxlength: undefined,
  step: '1',
  minlength: undefined,
  status: '',
  error: '',
  help: '',
  suggestion: '',
  suggestions: () => [],
  prop: '',
  name: null,
  title: null,
  required: null,
  trim: false,
  minimal: false,
  showCharacterLimit: false,
  optional: false,
  focusOnMount: false,
  loading: false,

  fetchSuggestions: undefined,
})

defineEmits<{
  keyup: [value: KeyboardEvent]
}>()

const model = defineModel<TModel>({ default: null })

const options = ref<TModel[]>([])
const optionsWidth = ref(0)
const popper = useTemplateRef('popper')
const previousQuery = ref<string | number | null>(null)

function input(value: TModel) {
  model.value = value
  fetch(value)
}

const fetch = debounce(
  async (value: TModel) => {
    if (value === previousQuery.value) return
    previousQuery.value = value
    if (props.fetchSuggestions) {
      options.value = await props.fetchSuggestions(value ?? '')
    }
  },
  300,
  { leading: true, trailing: true },
)

function onReferenceResize({ width }: DOMRectReadOnly) {
  optionsWidth.value = width
}

function onClickOption(option: TModel) {
  model.value = option
  popper.value?.hide()
}

function hidePopper() {
  popper.value?.hide()
}

onMounted(() => {
  if (popper.value?.reference) {
    useResizeObserver(popper.value.reference, onReferenceResize)
  }
})

defineExpose({
  popper,
  input,
})
</script>

<style>
.v-input--autocomplete.v-input--select .v-input__inner {
  color: initial;
}
</style>
